<script setup lang="ts">
const props = withDefaults(defineProps<{
  images: Array<{ url: string; altText: string | null }>
  title: string
  price: string
  category: string
  handle: string
  tags?: string[]
  dark?: boolean
  animated?: boolean
  visible?: boolean
  compact?: boolean
}>(), {
  dark: false,
  animated: false,
  visible: false,
  compact: false,
})

const isComingSoon = computed(() => props.tags?.includes('coming-soon') ?? false)

const IMAGE_LABELS = ['', 'Editorial', 'Lifestyle']
const CYCLE_DELAY = 1000
const activeIndex = ref(0)
let cycleTimer: ReturnType<typeof setTimeout> | null = null
const hovering = ref(false)
const canCycle = computed(() => props.images.length > 1)

function advanceTo(index: number) {
  if (index < props.images.length) {
    activeIndex.value = index
    cycleTimer = setTimeout(() => advanceTo(index + 1), CYCLE_DELAY)
  }
}

function onEnter() {
  if (!canCycle.value) return
  hovering.value = true
  clearTimer()
  cycleTimer = setTimeout(() => advanceTo(1), CYCLE_DELAY)
}

function onLeave() {
  hovering.value = false
  clearTimer()
  activeIndex.value = 0
}

function clearTimer() {
  if (cycleTimer !== null) {
    clearTimeout(cycleTimer)
    cycleTimer = null
  }
}

onUnmounted(() => clearTimer())
</script>

<template>
  <NuxtLink
    :to="`/shop/${handle}`"
    :class="[
      'group flex flex-col items-start overflow-hidden w-full h-full',
      animated && 'transition-all duration-500',
      animated && !visible && 'opacity-0 translate-y-6',
      animated && visible && 'opacity-100 translate-y-0',
    ]"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <!-- Image stack -->
    <div class="relative w-full overflow-hidden" style="aspect-ratio: 1 / 1;">
      <span v-if="isComingSoon" class="absolute top-3 left-3 z-10 font-label font-light text-sm uppercase tracking-widest bg-dark text-brand-light px-3 py-1">
        Coming Soon
      </span>
      <img
        v-for="(img, i) in images"
        :key="i"
        :src="img.url"
        :alt="img.altText || ''"
        :class="[
          'absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out',
          i === activeIndex ? 'opacity-100' : 'opacity-0',
        ]"
      />

      <!-- Overlay UI (hover only, multi-image only) -->
      <div
        v-if="canCycle"
        :class="[
          'absolute inset-x-0 bottom-0 flex items-end justify-between px-3 pb-3 transition-opacity duration-300',
          hovering ? 'opacity-100' : 'opacity-0',
        ]"
      >
        <!-- Label -->
        <span
          v-if="IMAGE_LABELS[activeIndex]"
          class="font-label font-light text-xs tracking-wider text-inverse drop-shadow-md"
        >
          {{ IMAGE_LABELS[activeIndex] }}
        </span>
        <span v-else />

        <!-- Dot indicators -->
        <div class="flex gap-1.5">
          <span
            v-for="(_, i) in images"
            :key="i"
            :class="[
              'size-1.5 rounded-full transition-colors duration-300',
              i === activeIndex ? 'bg-surface' : 'bg-surface/40',
            ]"
          />
        </div>
      </div>
    </div>

    <!-- Card info -->
    <div class="flex flex-col gap-2 items-start p-4 w-full">
      <p v-if="category && !compact" class="font-label font-light text-xs leading-4 tracking-wider text-brand">{{ category }}</p>
      <div class="flex items-end justify-between w-full whitespace-nowrap">
        <p :class="['font-serif font-normal text-lg md:text-xl leading-6 tracking-wide', dark ? 'text-inverse' : 'text-dark']">{{ title }}</p>
        <p v-if="price && !compact" class="font-sans font-light text-base md:text-md leading-7 text-brand">{{ price }}</p>
      </div>
      <AppButton v-if="!compact && !isComingSoon" type="Ghost" size="Small" label="View Product" :showIconRight="true" />
    </div>
  </NuxtLink>
</template>
